<template>
  <div class="accruals">
    <div class="hgroup">
      <h1 class="heading1">{{ $t('История начисления бонусов') }}</h1>
      <span class="title-info" v-if="historyStartedAt && !loading">
        {{ $t('Данные доступны с') }} {{ historyStartedAt | dateFormat }}
      </span>
      <span class="title-info" v-else-if="!accruals.length && !loading">
        {{ $t('Данных пока нет') }}
      </span>
    </div>
    <div class="spoiler">
      <TransitionExpand>
        <WhereIsMyAccurals
          v-show="show"
          :partner="card.partner"
        />
      </TransitionExpand>
      <span class="spoiler_link" @click="toggle">
        {{ show ? $t('Скрыть пояснения') : $t('Где мои бонусы?') }}
        <i class="spoiler_arrow" :class="{ 'spoiler_arrow__open': show }"></i>
      </span>
    </div>
    <div class="accruals_wrap">
      <AccrualsHistoryItem
        v-for="accrual in accruals"
        :key="accrual.accrualId"
        :accrual="accrual"
      />
    </div>
    <Spinner class="accruals_spinner" size="medium" color="red" v-if="loading" />
    <Button class="accruals_button" v-if="hasShowMoreButton" variant="outlined" @click="handleShowMore">
      {{ $t('Показать больше') }}
    </Button>
  </div>
</template>

<script>
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { State } from 'vuex-class'

import settings from '@/settings'
import Button from '@/common/components/Button'
import Spinner from '@/common/components/Spinner.vue'
import TransitionExpand from '@/common/components/transitions/TransitionExpand.vue'
import AccrualsHistoryItem from './AccrualsHistoryItem'
import WhereIsMyAccurals from './WhereIsMyAccurals/WhereIsMyAccurals'
import { fixIsoDate } from '@/helpers'

@Component({
  components: { Button, Spinner, AccrualsHistoryItem, TransitionExpand, WhereIsMyAccurals }
})
class AccrualsHistory extends Vue {
  @State('country') currentCountry

  @Prop({ type: Object, required: true }) card
  @Prop({ type: Array, default: () => [] }) accruals
  @Prop({ type: Object }) meta
  @Prop({ type: Boolean, default: false }) loading

  show = false

  toggle () {
    this.show = !this.show
    this.$emit('toggle', this.show)
  }

  /**
   * the button should only be displayed if there is still an records to display
   * @returns {boolean}
   */
  get hasShowMoreButton () {
    return this.accruals.length > 0 && this.meta.totalCount > this.accruals.length
  }

  /**
   * If card binding date (bound at) is later than the Date - "portal launch date",
   * then the phrase is not displayed at all.
   * @returns {string}
   */
  get historyStartedAt () {
    const launchDate = new Date(fixIsoDate(settings.LAUNCH_DATE))
    const bindDate = new Date(fixIsoDate(this.card.boundAt))
    return launchDate > bindDate ? settings.LAUNCH_DATE : null
  }

  @Emit('show-more')
  handleShowMore () {
    this.$analytics.trackAccrualsHistoryShowMoreButtonClick({ screenId: this.$route.name, game: this.card.currentGame })
    return { offset: this.meta.offset + this.meta.count, mergeData: true }
  }
}

export default AccrualsHistory
</script>

<style lang="scss">
.accruals {
  @extend %layout;
  margin-bottom: 40px;
  position: relative;
  @include media_tablet {
    margin-bottom: 100px;
  }

  &_wrap {
    margin: 0 -9.375vw;
    @include media_tablet {
      margin: 0 -7.8vw;
    }
    @include media_desktop {
      margin: 0 -3.66vw;
    }
  }
  &_row {
    background: rgba(255, 255, 255, .08);
    color: $font-color_major;
    display: grid;
    grid-template-columns: 20px 1fr 70px;
    font-size: 12px;
    line-height: 14px;
    margin-bottom: 3px;
    padding: 15px 9.375vw 18px;
    position: relative;
    transition: background-color $duration,
                color $duration;
    z-index: 0;

    &:hover {
      background: rgba(255, 255, 255, .15);
      color: #fff;
      z-index: 1;
    }
    @include media_tablet {
      grid-template-columns: 140px 1fr 100px;
      font-size: 18px;
      line-height: 36px;
      padding: 16px 7.8vw 23px;

      &:hover {
        box-shadow: 2px 0 15px rgba(0, 0, 0, .4),
                    0 0 1px rgba(255, 255, 255, .2) inset;
      }
    }
    @include media_desktop {
      grid-template-columns: 180px 245px 2fr 3fr;
      font-size: 16px;
      line-height: 18px;
      padding: 26px 3.66vw 25px;
    }
  }
  &_game {
    display: block;
    position: relative;
    @include media_tablet {
      padding-left: 31px;
    }
    @include media_desktop {
      grid-column: 1;
    }
    &:after {
      content: '';
      background: {
        repeat: no-repeat;
        size: contain;
      }
      height: 16px;
      width: 16px;
      opacity: .6;
      left: 0;
      position: absolute;
      top: -1px;
      transition: opacity $duration;
      @include media_tablet {
        height: 29px;
        width: 29px;
        top: 3px;
      }
      @include media_desktop {
        top: -6px;
      }

      .accruals_row:hover & {
        opacity: 1;
      }
    }
    &_name {
      display: none;
      @include media_tablet {
        display: block;
      }
      @include media_desktop {
        padding-left: 36px;
      }
    }
    &__mt:after {
      background-image: url("#{$common-images}/svg/mt.svg");
    }
    &__mk:after {
      background-image: url("#{$common-images}/svg/mk.svg");
    }
    &__tb:after {
      background-image: url("#{$common-images}/svg/tb.svg");
    }
  }
  &_title {
    display: block;
    color: #fff;
    @include media_tablet {
      padding-left: 10px;
    }
    @include media_desktop {
      grid-column: 3;
    }
  }
  &_date {
    display: block;
    font-size: 10px;
    line-height: 16px;
    text-align: right;
    @include media_tablet {
      font-size: 14px;
      line-height: 36px;
    }
    @include media_desktop {
      font-size: 16px;
      line-height: 18px;
      grid-column: 2;
      grid-row: 1;
      text-align: left;
    }
  }
  &_description {
    display: block;
    grid-row: 2;
    grid-column: 1;
    grid-column-start: 1;
    grid-column-end: 4;
    margin-top: 13px;
    @include media_tablet {
      grid-column: 2;
      grid-column-start: 1;
      grid-column-end: 3;
      font-size: 14px;
      line-height: 18px;
      margin-top: 3px;
    }
    @include media_desktop {
      grid-row: 1;
      grid-column: 4;
      grid-column-start: 4;
      grid-column-end: 4;
      font-size: 16px;
      line-height: 18px;
      margin-top: 0;
    }
  }
  &_button {
    margin-top: 20px;
  }
  &_spinner {
    position: absolute;
    bottom: -35px;
    left: 50%;
    @include media_tablet {
      bottom: -50px;
    }
  }
}

.spoiler {
  margin: -20px 0 25px;
  @include media_tablet {
    margin: -26px 0 35px;
  }
  &_link {
    border-bottom: 1px dashed;
    cursor: pointer;
    display: inline-block;
    font: 12px / 1.5 $font-roboto;
    padding: 0;
    position: relative;
    transition: color $duration;
    @include media_tablet {
      font-size: 16px;
      padding: 0;
    }
    &:hover {
      color: $link-color_hover;
    }
  }
  &_arrow {
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
    border-top: 5px solid;
    position: absolute;
    right: -10px;
    top: 7px;
    transition: transform $duration linear;
    @include media_tablet {
      top: 11px;
    }

    &__open {
      transform: rotate(180deg);
    }
  }
  &_content {
    font-size: 12px;
    padding-bottom: 15px;
    @include media_tablet {
      font-size: 16px;
    }
  }
}
</style>
